import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValue, animate } from "framer-motion";
import { ArrowDown, MapPin, TrendingDown } from "lucide-react";
import { API, fmt, scrollToId } from "../lib/store";

export const EASE = [0.16, 1, 0.3, 1];

const HERO_IMG =
    "https://static.prod-images.emergentagent.com/jobs/9b700006-9adc-4ff6-aa7d-58905762429f/images/87c6ade9263a7ee39680acf20a3b8e5243d25974fd70ef69671188f1ab826503.jpeg";

const LINES = [
    { text: "EVERY PRICE", cls: "text-white", tid: "hero-headline-line-1" },
    { text: "40% UNDER", cls: "text-gold", tid: "hero-headline-line-2" },
    { text: "RETAIL.", cls: "text-outline", tid: "hero-headline-line-3" },
];

function Counter({ value, prefix = "", suffix = "" }) {
    const [display, setDisplay] = useState(0);
    useEffect(() => {
        if (value == null) return;
        const controls = animate(0, value, {
            duration: 1.8,
            delay: 0.8,
            ease: EASE,
            onUpdate: (v) => setDisplay(v),
        });
        return () => controls.stop();
    }, [value]);
    return (
        <span className="font-display text-2xl md:text-4xl font-bold text-white">
            {prefix}
            {value == null ? "—" : Math.round(display).toLocaleString("en-US")}
            {suffix}
        </span>
    );
}

export default function Hero() {
    const sectionRef = useRef(null);
    const frameRef = useRef(null);
    const [meta, setMeta] = useState(null);

    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
    const glowY = useTransform(scrollYProgress, [0, 1], [0, -70]);

    const rawRX = useMotionValue(0);
    const rawRY = useMotionValue(0);
    const rotateX = useSpring(rawRX, { stiffness: 55, damping: 14 });
    const rotateY = useSpring(rawRY, { stiffness: 55, damping: 14 });

    useEffect(() => {
        fetch(`${API}/meta`)
            .then((r) => r.json())
            .then(setMeta)
            .catch(() => setMeta(null));
    }, []);

    const onMove = (e) => {
        const r = frameRef.current?.getBoundingClientRect();
        if (!r) return;
        rawRY.set(((e.clientX - r.left) / r.width - 0.5) * 10);
        rawRX.set(-((e.clientY - r.top) / r.height - 0.5) * 10);
    };
    const onLeave = () => {
        rawRX.set(0);
        rawRY.set(0);
    };

    return (
        <section
            ref={sectionRef}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-24 pb-16 md:pt-28"
        >
            {/* backdrop grid */}
            <div
                className="absolute inset-0 opacity-[0.35]"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)",
                    backgroundSize: "72px 72px",
                }}
            />
            <div className="absolute -top-40 left-1/4 h-[500px] w-[500px] rounded-full bg-gold/10 blur-[140px]" />

            <div className="relative mx-auto w-full max-w-[1440px] px-5 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                {/* left — kinetic type */}
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
                        data-testid="hero-value-prop-badge"
                        className="mb-8 inline-flex items-center gap-3 rounded-full border border-gold/40 bg-gold/10 px-4 py-2"
                    >
                        <MapPin className="h-3.5 w-3.5 text-gold" />
                        <span className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-gold">
                            Clifton, New Jersey — Appliance Showroom
                        </span>
                    </motion.div>

                    <h1 className="font-display font-extrabold tracking-tight leading-[0.92] text-[15vw] sm:text-7xl lg:text-8xl xl:text-[7.5rem]">
                        {LINES.map((line, i) => (
                            <span key={line.tid} className="block overflow-hidden pb-1">
                                <motion.span
                                    data-testid={line.tid}
                                    className={`block ${line.cls}`}
                                    initial={{ y: "112%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 1, delay: 0.25 + i * 0.13, ease: EASE }}
                                >
                                    {line.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
                        className="mt-7 max-w-xl text-sm md:text-lg text-slate-300 leading-relaxed"
                    >
                        Fridges, gas ranges, wall ovens, microwaves, dishwashers — the brands your
                        kitchen actually wants, at prices retail can't touch. Same models,
                        same warranties. About 40%+ less — plus delivery available.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-cta-explore-catalog"
                            onClick={() => scrollToId("inventory")}
                            className="group flex items-center gap-3 rounded-full bg-gold px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-ink transition-all duration-300 hover:bg-white hover:gap-5"
                        >
                            Explore the Inventory
                            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
                        </button>
                        <button
                            data-testid="hero-cta-visit-showroom"
                            onClick={() => scrollToId("showroom")}
                            className="rounded-full border border-white/20 px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:border-gold hover:text-gold"
                        >
                            Visit the Showroom
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.15 }}
                        className="mt-14 grid grid-cols-3 gap-6 max-w-xl border-t border-white/10 pt-7"
                    >
                        <div>
                            <Counter value={meta?.products_count} />
                            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                Appliances on the floor
                            </p>
                        </div>
                        <div>
                            <Counter value={meta?.avg_savings_pct} suffix="%" />
                            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                Under retail, avg.
                            </p>
                        </div>
                        <div>
                            <Counter value={meta?.total_savings} prefix="$" />
                            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                Total savings on the floor
                            </p>
                        </div>
                    </motion.div>
                </div>

                {/* right — 3D spotlight product */}
                <div className="lg:col-span-5">
                    <motion.div
                        ref={frameRef}
                        style={{ rotateX, rotateY, transformPerspective: 1000 }}
                        className="relative mx-auto max-w-md lg:max-w-none"
                        initial={{ opacity: 0, scale: 0.94 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
                    >
                        <motion.div
                            style={{ y: glowY }}
                            className="absolute inset-8 rounded-full bg-gold/20 blur-[90px]"
                        />
                        <div className="relative clip-corner border border-white/10 bg-ink2">
                            <motion.img
                                style={{ y: imgY }}
                                src={HERO_IMG}
                                alt="Stainless steel French door refrigerator under a spotlight"
                                className="w-full aspect-square object-cover scale-[1.12]"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
                            <span className="absolute left-0 top-8 h-4 w-4 border-l border-t border-gold/70" />
                            <span className="absolute right-0 top-8 h-4 w-4 border-r border-t border-gold/70" />
                            <span className="absolute bottom-[64px] left-0 h-4 w-4 border-b border-l border-gold/70" />
                            <span className="absolute bottom-[64px] right-0 h-4 w-4 border-b border-r border-gold/70" />
                        </div>

                        <motion.div
                            animate={{ y: [0, -10, 0] }}
                            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute -bottom-6 -left-4 md:-left-10 flex items-center gap-3 rounded-full border border-white/10 bg-ink/90 backdrop-blur-xl px-5 py-3.5 shadow-2xl"
                        >
                            <TrendingDown className="h-4 w-4 text-save" />
                            <div className="leading-tight">
                                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                                    Same model, same warranty
                                </p>
                                <p className="font-mono text-xs font-bold text-save">
                                    $800 LESS THAN RETAIL
                                </p>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
